import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "kb-theme";

export type Theme = "light" | "dark";

function preferredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;

  // No stored choice means follow the OS, so the docs match whatever the reader
  // already had open next to them.
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Light/dark for the docs site.
 *
 * RDS ships `rigo` and `rigo-dark` as CSS custom properties switched by a `dark`
 * class on the root element, so this toggles that class rather than swapping a
 * provider.
 */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => preferredTheme());

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((current) => (current === "dark" ? "light" : "dark")),
    [],
  );

  return { theme, toggle };
}
